import React, { useState, useEffect } from "react";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  TextField,
  Collapse,
} from "@mui/material";
import {
  ArrowBack,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  Delete as DeleteIcon,
  FileDownload as MultiExportIcon,
  FilterList as FilterIcon,
  ClearAll as ClearIcon,
  ChildCare as ChildrenIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../config/api";
import { exportToCSV, generateSafeFilename, formatDate, formatarValorCSV } from "../utils/csvExport";

export function AdminInscricoes() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [produtos, setProdutos] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inscricaoDetalhes, setInscricaoDetalhes] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editandoStatus, setEditandoStatus] = useState(null);
  const [novoStatus, setNovoStatus] = useState("");
  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // Estados para exportação de múltiplos produtos
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [produtosSelecionadosExport, setProdutosSelecionadosExport] = useState([]);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingCriancas, setLoadingCriancas] = useState(false);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    statusPagamento: "",
    formaPagamento: "",
    tipoProduto: "",
    subtipoProduto: "",
    buscarNome: "",
    valorMin: "",
    valorMax: "",
    dataInicio: "",
    dataFim: "",
    somenteNaoConferidos: false
  });
  const [inscricoesFiltradas, setInscricoesFiltradas] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [conferindoIds, setConferindoIds] = useState([]);

  // Buscar produtos
  useEffect(() => {
    buscarProdutos();
  }, []);

  const buscarProdutos = async () => {
    try {
      const response = await fetch(getApiUrl("/api/checkout/produtos?ativo=true"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Filtro adicional no frontend para garantir apenas produtos ativos
        const produtosAtivos = result.data.filter(produto => produto.ativo === true);
        setProdutos(produtosAtivos);
      }
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
    }
  };

  // Buscar inscrições
  const buscarInscricoes = async () => {
    if (!produtoSelecionado) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl("/api/inscricoes"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Achatar inscrições - cada inscrito vira uma linha
        const inscricoesAchatadas = [];
        result.data.forEach(inscricao => {
          if (inscricao.inscricoes) {
            inscricao.inscricoes.forEach(item => {
              if (item.produto === produtoSelecionado || item.produto?._id === produtoSelecionado) {
                inscricoesAchatadas.push({
                  nomeInscrito: item.nomeInscrito,
                  nomePagante: inscricao.nomePagante,
                  emailPagante: inscricao.emailPagante,
                  telefonePagante: inscricao.telefonePagante || "",
                  valorTotal: inscricao.valorTotal,
                  tipoProduto: item.tipoProduto,
                  subtipoProduto: item.subtipoProduto,
                  valorProduto: item.valorProduto,
                  statusPagamento: inscricao.statusPagamento,
                  createdAt: inscricao.createdAt,
                  transacaoId: inscricao.mercadoPago?.paymentId || inscricao.transacaoId || inscricao._id,
                  paymentId: inscricao.mercadoPago?.paymentId,
                  conferido: inscricao.conferido || false,
                  conferidoEm: inscricao.conferidoEm || null,
                  conferidoPor: inscricao.conferidoPor || "",
                  criancas: inscricao.criancas || [],
                  inscricaoCompleta: inscricao
                });
              }
            });
          }
        });
        setInscricoes(inscricoesAchatadas);
        setInscricoesFiltradas(inscricoesAchatadas); // Inicializar filtradas
      } else {
        setError("Erro ao carregar inscrições");
      }
    } catch (error) {
      setError("Erro ao conectar com servidor");
    } finally {
      setLoading(false);
    }
  };

  // Executar busca quando produto selecionado mudar
  useEffect(() => {
    if (produtoSelecionado) {
      buscarInscricoes();
    } else {
      setInscricoes([]);
      setInscricoesFiltradas([]);
    }
  }, [produtoSelecionado]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplicar filtros sempre que inscricoes ou filtros mudarem
  useEffect(() => {
    aplicarFiltros();
  }, [inscricoes, filtros]); // eslint-disable-line react-hooks/exhaustive-deps

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    let resultado = [...inscricoes];

    // Filtro por status
    if (filtros.statusPagamento) {
      resultado = resultado.filter(inscricao => 
        inscricao.statusPagamento === filtros.statusPagamento
      );
    }

    // Filtro por forma de pagamento
    if (filtros.formaPagamento) {
      resultado = resultado.filter(inscricao => 
        (inscricao.inscricaoCompleta?.formaPagamento || "pix") === filtros.formaPagamento
      );
    }

    // Filtro por tipo de produto
    if (filtros.tipoProduto) {
      resultado = resultado.filter(inscricao => 
        inscricao.tipoProduto === filtros.tipoProduto
      );
    }

    // Filtro por subtipo de produto
    if (filtros.subtipoProduto) {
      resultado = resultado.filter(inscricao => 
        inscricao.subtipoProduto === filtros.subtipoProduto
      );
    }

    // Filtro por nome (inscrito ou pagador)
    if (filtros.buscarNome) {
      const termo = filtros.buscarNome.toLowerCase();
      resultado = resultado.filter(inscricao => 
        inscricao.nomeInscrito.toLowerCase().includes(termo) ||
        inscricao.nomePagante.toLowerCase().includes(termo)
      );
    }

    // Filtro por valor mínimo
    if (filtros.valorMin) {
      const valorMin = parseFloat(filtros.valorMin);
      resultado = resultado.filter(inscricao => 
        inscricao.valorProduto >= valorMin
      );
    }

    // Filtro por valor máximo
    if (filtros.valorMax) {
      const valorMax = parseFloat(filtros.valorMax);
      resultado = resultado.filter(inscricao => 
        inscricao.valorProduto <= valorMax
      );
    }

    // Filtro por data de início
    if (filtros.dataInicio) {
      const dataInicio = new Date(filtros.dataInicio);
      dataInicio.setHours(0, 0, 0, 0);
      resultado = resultado.filter(inscricao => {
        const dataInscricao = new Date(inscricao.createdAt);
        return dataInscricao >= dataInicio;
      });
    }

    // Filtro por data de fim
    if (filtros.dataFim) {
      const dataFim = new Date(filtros.dataFim);
      dataFim.setHours(23, 59, 59, 999);
      resultado = resultado.filter(inscricao => {
        const dataInscricao = new Date(inscricao.createdAt);
        return dataInscricao <= dataFim;
      });
    }

    // Filtro: somente inscrições ainda não conferidas
    if (filtros.somenteNaoConferidos) {
      resultado = resultado.filter(inscricao => !inscricao.conferido);
    }

    setInscricoesFiltradas(resultado);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      statusPagamento: "",
      formaPagamento: "",
      tipoProduto: "",
      subtipoProduto: "",
      buscarNome: "",
      valorMin: "",
      valorMax: "",
      dataInicio: "",
      dataFim: "",
      somenteNaoConferidos: false
    });
  };

  // Obter valores únicos para filtros de select
  const obterValoresUnicos = (campo) => {
    const valores = inscricoes.map(inscricao => {
      if (campo === 'formaPagamento') {
        return inscricao.inscricaoCompleta?.formaPagamento || "pix";
      }
      return inscricao[campo];
    });
    return [...new Set(valores)].filter(Boolean).sort();
  };

  // Visualizar detalhes da inscrição
  const visualizarDetalhes = (inscricao) => {
    setInscricaoDetalhes(inscricao);
    setOpenDialog(true);
  };

  // Funções para editar status
  const iniciarEdicaoStatus = (inscricao) => {
    setEditandoStatus(inscricao.inscricaoCompleta._id);
    setNovoStatus(inscricao.statusPagamento);
  };

  const cancelarEdicaoStatus = () => {
    setEditandoStatus(null);
    setNovoStatus("");
  };

  const salvarStatus = async (inscricaoId) => {
    setLoadingStatusUpdate(true);
    setError(""); // Limpar erros anteriores
    
    console.log('Salvando status:', { inscricaoId, novoStatus });
    
    try {
      const url = getApiUrl(`/api/pagamentos?action=status&id=${inscricaoId}`);
      console.log('URL da requisição:', url);
      
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ statusPagamento: novoStatus }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('Resultado:', result);

      if (result.success) {
        // Atualizar a lista local
        const novasInscricoes = inscricoes.map(item => 
          item.inscricaoCompleta._id === inscricaoId 
            ? { ...item, statusPagamento: novoStatus }
            : item
        );
        setInscricoes(novasInscricoes);
        setEditandoStatus(null);
        setNovoStatus("");
      } else {
        setError(result.error || "Erro ao atualizar status");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      setError(`Erro ao atualizar status: ${error.message}`);
    } finally {
      setLoadingStatusUpdate(false);
    }
  };

  // Alternar conferência de uma transação (Inscricao). Como o relatório é
  // achatado por inscrito, todas as linhas da mesma transação são atualizadas
  // juntas.
  const alternarConferido = async (inscricaoId, valorAtual) => {
    const novoValor = !valorAtual;

    // Atualização otimista de todas as linhas dessa transação.
    const aplicarLocal = (valor) => {
      setInscricoes(prev =>
        prev.map(item =>
          item.inscricaoCompleta._id === inscricaoId
            ? {
                ...item,
                conferido: valor,
                inscricaoCompleta: { ...item.inscricaoCompleta, conferido: valor },
              }
            : item
        )
      );
    };

    setConferindoIds(prev => [...prev, inscricaoId]);
    aplicarLocal(novoValor);

    try {
      const response = await fetch(
        getApiUrl(`/api/inscricoes?id=${inscricaoId}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ conferido: novoValor }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Erro ${response.status}`);
      }
    } catch (error) {
      console.error("Erro ao atualizar conferência:", error);
      setError(`Erro ao atualizar conferência: ${error.message}`);
      aplicarLocal(valorAtual); // Reverter em caso de falha
    } finally {
      setConferindoIds(prev => prev.filter(id => id !== inscricaoId));
    }
  };

  // Função para excluir transação cancelada
  const excluirTransacao = async (inscricaoId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta transação cancelada? Esta ação não pode ser desfeita.')) {
      return;
    }

    setLoadingDelete(true);
    setError("");
    
    try {
      const url = getApiUrl(`/api/inscricoes?id=${inscricaoId}`);
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Remover da lista local
        const novasInscricoes = inscricoes.filter(item => 
          item.inscricaoCompleta._id !== inscricaoId
        );
        setInscricoes(novasInscricoes);
      } else {
        setError(result.error || "Erro ao excluir transação");
      }
    } catch (error) {
      console.error("Erro ao excluir transação:", error);
      setError(`Erro ao excluir transação: ${error.message}`);
    } finally {
      setLoadingDelete(false);
    }
  };

  // Formatar data
  const formatarData = (data) => {
    return formatDate(data);
  };

  // Status chip
  const getStatusChip = (status) => {
    const statusMap = {
      pendente: { color: "warning", label: "Pendente" },
      processando: { color: "info", label: "Processando" },
      aprovado: { color: "success", label: "Aprovado" },
      rejeitado: { color: "error", label: "Rejeitado" },
      cancelado: { color: "default", label: "Cancelado" },
    };
    
    const config = statusMap[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Forma de pagamento chip
  const getFormaPagamentoChip = (formaPagamento) => {
    const formasMap = {
      pix: { color: "success", label: "PIX" },
      cartao_credito: { color: "primary", label: "Cartão" },
      dinheiro: { color: "secondary", label: "Dinheiro" },
    };
    
    const config = formasMap[formaPagamento] || { color: "default", label: formaPagamento };
    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
  };

  // Exportar para CSV (usando dados filtrados)
  const exportarCSV = () => {
    const dadosExportacao = inscricoesFiltradas.length > 0 ? inscricoesFiltradas : inscricoes;
    const headers = [
      "Nome Inscrito",
      "Pagador",
      "Contato",
      "Valor Produto",
      "Tipo",
      "Subtipo",
      "Status",
      "Forma de Pagamento",
      "Data",
      "ID Transação",
      "Conferido",
      "Conferido Por"
    ];

    const csvData = dadosExportacao.map(inscricao => [
      inscricao.nomeInscrito,
      inscricao.nomePagante,
      inscricao.telefonePagante || "",
      formatarValorCSV(inscricao.valorProduto),
      inscricao.tipoProduto,
      inscricao.subtipoProduto,
      inscricao.statusPagamento,
      inscricao.inscricaoCompleta?.formaPagamento || "pix",
      formatarData(inscricao.createdAt),
      inscricao.paymentId || inscricao.transacaoId?.substring(0, 8) || "N/A",
      inscricao.conferido ? "Sim" : "Não",
      inscricao.conferidoPor || ""
    ]);

    const filename = generateSafeFilename(produtoNome, "inscricoes", true);
    exportToCSV(csvData, headers, filename);
  };

  // Toggle seleção de produto para exportação
  const toggleProdutoExport = (produtoId) => {
    setProdutosSelecionadosExport(prev => {
      if (prev.includes(produtoId)) {
        return prev.filter(id => id !== produtoId);
      }
      return [...prev, produtoId];
    });
  };

  // Selecionar/Desmarcar todos os produtos
  const toggleTodosProdutos = () => {
    if (produtosSelecionadosExport.length === produtos.length) {
      setProdutosSelecionadosExport([]);
    } else {
      setProdutosSelecionadosExport(produtos.map(p => p._id));
    }
  };

  // Exportar CSV de múltiplos produtos
  const exportarCSVMultiplos = async () => {
    if (produtosSelecionadosExport.length === 0) {
      alert("Selecione pelo menos um produto para exportar!");
      return;
    }

    setLoadingExport(true);
    
    try {
      const response = await fetch(getApiUrl("/api/inscricoes"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      
      const result = await response.json();
      
      if (!result.success) {
        alert("Erro ao buscar inscrições");
        return;
      }

      // Achatar inscrições para produtos selecionados
      const inscricoesConsolidadas = [];
      
      result.data.forEach(inscricao => {
        if (inscricao.inscricoes) {
          inscricao.inscricoes.forEach(item => {
            const produtoId = item.produto?._id || item.produto;
            if (produtosSelecionadosExport.includes(produtoId)) {
              // Buscar nome do produto
              const produtoInfo = produtos.find(p => p._id === produtoId);
              inscricoesConsolidadas.push({
                nomeProduto: produtoInfo?.nome || "Produto não encontrado",
                nomeInscrito: item.nomeInscrito,
                nomePagante: inscricao.nomePagante,
                emailPagante: inscricao.emailPagante,
                telefonePagante: inscricao.telefonePagante || "",
                valorProduto: item.valorProduto,
                tipoProduto: item.tipoProduto,
                subtipoProduto: item.subtipoProduto,
                statusPagamento: inscricao.statusPagamento,
                formaPagamento: inscricao.formaPagamento || "pix",
                createdAt: inscricao.createdAt,
                paymentId: inscricao.mercadoPago?.paymentId || inscricao.transacaoId || inscricao._id,
                conferido: inscricao.conferido || false,
                conferidoPor: inscricao.conferidoPor || "",
              });
            }
          });
        }
      });

      if (inscricoesConsolidadas.length === 0) {
        alert("Nenhuma inscrição encontrada para os produtos selecionados!");
        return;
      }

      const headers = [
        "Produto",
        "Nome Inscrito",
        "Pagador",
        "Email",
        "Contato",
        "Valor Produto",
        "Tipo",
        "Subtipo",
        "Status",
        "Forma de Pagamento",
        "Data",
        "ID Transação",
        "Conferido",
        "Conferido Por"
      ];

      const csvData = inscricoesConsolidadas.map(inscricao => [
        inscricao.nomeProduto,
        inscricao.nomeInscrito,
        inscricao.nomePagante,
        inscricao.emailPagante,
        inscricao.telefonePagante || "",
        formatarValorCSV(inscricao.valorProduto),
        inscricao.tipoProduto,
        inscricao.subtipoProduto,
        inscricao.statusPagamento,
        inscricao.formaPagamento,
        formatarData(inscricao.createdAt),
        inscricao.paymentId?.toString().substring(0, 12) || "N/A",
        inscricao.conferido ? "Sim" : "Não",
        inscricao.conferidoPor || ""
      ]);

      const nomesProdutos = produtosSelecionadosExport.length === 1
        ? produtos.find(p => p._id === produtosSelecionadosExport[0])?.nome || "produto"
        : `${produtosSelecionadosExport.length}_produtos`;
      
      const filename = generateSafeFilename(nomesProdutos, "inscricoes_consolidado", true);
      exportToCSV(csvData, headers, filename);
      
      setOpenExportDialog(false);
      setProdutosSelecionadosExport([]);
    } catch (error) {
      console.error("Erro ao exportar:", error);
      alert("Erro ao exportar inscrições");
    } finally {
      setLoadingExport(false);
    }
  };

  // Exportar CSV de crianças (todas as inscrições): responsável, criança, idade
  const exportarCriancas = async () => {
    setLoadingCriancas(true);

    try {
      const response = await fetch(getApiUrl("/api/inscricoes"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const result = await response.json();

      if (!result.success) {
        alert("Erro ao buscar inscrições");
        return;
      }

      const linhas = [];
      result.data.forEach((inscricao) => {
        (inscricao.criancas || []).forEach((crianca) => {
          linhas.push([
            inscricao.nomePagante,
            inscricao.telefonePagante || "",
            inscricao.emailPagante || "",
            crianca.nome,
            crianca.idade || "",
          ]);
        });
      });

      if (linhas.length === 0) {
        alert("Nenhuma criança inscrita encontrada.");
        return;
      }

      const headers = [
        "Responsável",
        "Telefone",
        "Email",
        "Criança",
        "Idade",
      ];

      const filename = generateSafeFilename("criancas", "inscritas", true);
      exportToCSV(linhas, headers, filename);
    } catch (error) {
      console.error("Erro ao exportar crianças:", error);
      alert("Erro ao exportar crianças");
    } finally {
      setLoadingCriancas(false);
    }
  };

  // Calcular estatísticas (usando dados filtrados)
  const calcularEstatisticas = () => {
    const dados = inscricoesFiltradas.length > 0 ? inscricoesFiltradas : inscricoes;
    const total = dados.length;
    const aprovadas = dados.filter(i => i.statusPagamento === "aprovado").length;
    const valorTotal = dados
      .filter(i => i.statusPagamento === "aprovado")
      .reduce((sum, i) => sum + i.valorProduto, 0);
    
    // Calcular valores separados por forma de pagamento
    const inscricoesAprovadas = dados.filter(i => i.statusPagamento === "aprovado");
    const valorPix = inscricoesAprovadas
      .filter(i => i.inscricaoCompleta?.formaPagamento === "pix")
      .reduce((sum, i) => sum + i.valorProduto, 0);
    const valorOutrosMetodos = inscricoesAprovadas
      .filter(i => i.inscricaoCompleta?.formaPagamento !== "pix")
      .reduce((sum, i) => sum + i.valorProduto, 0);
    
    // Aplicar taxa de 1% apenas no PIX
    const valorLiquido = (valorPix * 0.99) + valorOutrosMetodos;

    // Total de crianças: as crianças são por transação, mas o relatório é
    // achatado por inscrito, então deduplicamos por _id da inscrição.
    const transacoesContadas = new Set();
    let totalCriancas = 0;
    dados.forEach((i) => {
      const id = i.inscricaoCompleta?._id;
      if (id && !transacoesContadas.has(id)) {
        transacoesContadas.add(id);
        totalCriancas += i.criancas?.length || 0;
      }
    });

    return { total, aprovadas, valorTotal, valorLiquido, valorPix, valorOutrosMetodos, totalCriancas };
  };

  const stats = calcularEstatisticas();
  const produtoNome = produtos.find(p => p._id === produtoSelecionado)?.nome || "";

  // Estilos das seções do modal de detalhes
  const secaoDetalheSx = {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    backgroundColor: "#fff",
  };
  const tituloSecaoSx = {
    color: "primary.main",
    fontWeight: "bold",
    letterSpacing: 0.5,
  };

  return (
    <InternalLayout>
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 2, md: 4 },
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >
        {/* Header */}
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item>
            <IconButton onClick={() => navigate("/dashboard")} sx={{ color: "primary.main" }}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" sx={{ color: "primary.main", fontWeight: "bold" }}>
              Relatório de Inscrições
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Visualize inscrições realizadas por produto
            </Typography>
          </Grid>
        </Grid>

        {/* Filtros */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item sx={{ width: { xs: "100%", md: "40%" } }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Selecionar Produto</InputLabel>
                  <Select
                    value={produtoSelecionado}
                    onChange={(e) => setProdutoSelecionado(e.target.value)}
                    label="Selecionar Produto"
                  >
                    <MenuItem value="">
                      <em>Selecione um produto...</em>
                    </MenuItem>
                    {produtos.map((produto) => (
                      <MenuItem key={produto._id} value={produto._id}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {produto.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {produto.tipo} - {produto.subtipo} - R$ {produto.valor.toFixed(2)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {produtoSelecionado && (
                    <Button
                      variant="outlined"
                      startIcon={<ExportIcon />}
                      onClick={exportarCSV}
                      sx={{ 
                        color: "primary.main",
                        borderColor: "primary.main",
                        width: { xs: '100%', md: 'auto' }
                      }}
                    >
                      Exportar CSV
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<MultiExportIcon />}
                    onClick={() => setOpenExportDialog(true)}
                    sx={{
                      width: { xs: '100%', md: 'auto' }
                    }}
                  >
                    Exportar Múltiplos Produtos
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={
                      loadingCriancas ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ChildrenIcon />
                      )
                    }
                    onClick={exportarCriancas}
                    disabled={loadingCriancas}
                    sx={{
                      color: "primary.main",
                      borderColor: "primary.main",
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    {loadingCriancas ? "Exportando..." : "Exportar Crianças"}
                  </Button>
                  {produtoSelecionado && (
                    <Button
                      variant={mostrarFiltros ? "contained" : "outlined"}
                      startIcon={<FilterIcon />}
                      onClick={() => setMostrarFiltros(!mostrarFiltros)}
                      sx={{ 
                        width: { xs: '100%', md: 'auto' }
                      }}
                    >
                      Filtros {inscricoesFiltradas.length < inscricoes.length ? `(${inscricoesFiltradas.length}/${inscricoes.length})` : ''}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Painel de Filtros Expandido */}
        {produtoSelecionado && (
          <Collapse in={mostrarFiltros}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: "primary.main" }}>
                    Filtros Avançados
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={limparFiltros}
                    size="small"
                  >
                    Limpar Filtros
                  </Button>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Primeira linha */}
                  <Grid item sx={{ width: { xs: "100%", sm: "48%", md: "23%" } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status Pagamento</InputLabel>
                      <Select
                        value={filtros.statusPagamento}
                        onChange={(e) => setFiltros(prev => ({ ...prev, statusPagamento: e.target.value }))}
                        label="Status Pagamento"
                      >
                        <MenuItem value="">
                          <em>Todos os status</em>
                        </MenuItem>
                        <MenuItem value="pendente">Pendente</MenuItem>
                        <MenuItem value="processando">Processando</MenuItem>
                        <MenuItem value="aprovado">Aprovado</MenuItem>
                        <MenuItem value="rejeitado">Rejeitado</MenuItem>
                        <MenuItem value="cancelado">Cancelado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item sx={{ width: { xs: "100%", sm: "48%", md: "23%" } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Forma de Pagamento</InputLabel>
                      <Select
                        value={filtros.formaPagamento}
                        onChange={(e) => setFiltros(prev => ({ ...prev, formaPagamento: e.target.value }))}
                        label="Forma de Pagamento"
                      >
                        <MenuItem value="">
                          <em>Todas as formas</em>
                        </MenuItem>
                        <MenuItem value="pix">PIX</MenuItem>
                        <MenuItem value="cartao_credito">Cartão de Crédito</MenuItem>
                        <MenuItem value="dinheiro">Dinheiro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", sm: "48%", md: "23%" } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Produto</InputLabel>
                      <Select
                        value={filtros.tipoProduto}
                        onChange={(e) => setFiltros(prev => ({ ...prev, tipoProduto: e.target.value }))}
                        label="Tipo Produto"
                      >
                        <MenuItem value="">
                          <em>Todos os tipos</em>
                        </MenuItem>
                        {obterValoresUnicos('tipoProduto').map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", sm: "48%", md: "23%" } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Subtipo Produto</InputLabel>
                      <Select
                        value={filtros.subtipoProduto}
                        onChange={(e) => setFiltros(prev => ({ ...prev, subtipoProduto: e.target.value }))}
                        label="Subtipo Produto"
                      >
                        <MenuItem value="">
                          <em>Todos os subtipos</em>
                        </MenuItem>
                        {obterValoresUnicos('subtipoProduto').map((subtipo) => (
                          <MenuItem key={subtipo} value={subtipo}>{subtipo}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Segunda linha */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Buscar por nome"
                      placeholder="Nome do inscrito ou pagador..."
                      value={filtros.buscarNome}
                      onChange={(e) => setFiltros(prev => ({ ...prev, buscarNome: e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Valor mínimo"
                      type="number"
                      placeholder="0.00"
                      value={filtros.valorMin}
                      onChange={(e) => setFiltros(prev => ({ ...prev, valorMin: e.target.value }))}
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Valor máximo"
                      type="number"
                      placeholder="999.00"
                      value={filtros.valorMax}
                      onChange={(e) => setFiltros(prev => ({ ...prev, valorMax: e.target.value }))}
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Data Início"
                      type="date"
                      value={filtros.dataInicio}
                      onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Data Fim"
                      type="date"
                      value={filtros.dataFim}
                      onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filtros.somenteNaoConferidos}
                          onChange={(e) => setFiltros(prev => ({ ...prev, somenteNaoConferidos: e.target.checked }))}
                          color="success"
                        />
                      }
                      label="Exibir somente não conferidos"
                    />
                  </Grid>
                </Grid>
                
                {/* Indicador de filtros ativos */}
                {(inscricoesFiltradas.length < inscricoes.length) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Exibindo {inscricoesFiltradas.length} de {inscricoes.length} inscrições (filtros aplicados)
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Collapse>
        )}

        {/* Estatísticas */}
        {produtoSelecionado && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" sx={{ color: "primary.main", fontWeight: "bold" }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Inscrições
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" sx={{ color: "secondary.main", fontWeight: "bold" }}>
                    {stats.aprovadas}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inscrições Aprovadas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" sx={{ color: "secondary.main", fontWeight: "bold" }}>
                    R$ {stats.valorTotal.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valor Total Arrecadado
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" sx={{ color: "secondary.main", fontWeight: "bold" }}>
                    R$ {stats.valorLiquido.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valor Líquido (1% de taxa apenas no PIX)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" sx={{ color: "primary.main", fontWeight: "bold" }}>
                    {stats.totalCriancas}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Crianças Inscritas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabela de Inscrições */}
        {produtoSelecionado && (
          <Card sx={{ 
            overflow: 'hidden',
            '& .MuiCardContent-root': {
              padding: { xs: 1, sm: 2 }
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                Inscrições para: {produtoNome}
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                  {error}
                </Alert>
              )}
              
              {/* Layout Mobile - Cards */}
              {isMobile ? (
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  {inscricoesFiltradas.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      {produtoSelecionado ? 
                        (inscricoes.length === 0 ? "Nenhuma inscrição encontrada para este produto" : "Nenhuma inscrição corresponde aos filtros aplicados") 
                        : "Selecione um produto"}
                    </Typography>
                  ) : (
                    inscricoesFiltradas.map((inscricao, index) => (
                      <Card
                        key={`${inscricao.transacaoId}-${index}`}
                        sx={{
                          mb: 2,
                          border: '1px solid #e0e0e0',
                          backgroundColor: inscricao.conferido ? '#e8f5e9' : undefined,
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!inscricao.conferido}
                                onChange={() =>
                                  alternarConferido(
                                    inscricao.inscricaoCompleta._id,
                                    inscricao.conferido
                                  )
                                }
                                disabled={conferindoIds.includes(inscricao.inscricaoCompleta._id)}
                                color="success"
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {inscricao.conferido ? 'Conferido' : 'Marcar como conferido'}
                              </Typography>
                            }
                            sx={{ mb: 1 }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                {inscricao.nomeInscrito}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pago por: {inscricao.nomePagante}
                              </Typography>
                              {inscricao.criancas?.length > 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ color: "text.secondary" }}
                                >
                                  👶 {inscricao.criancas.length} criança(s)
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" sx={{ color: 'secondary.main', fontSize: '1rem' }}>
                                R$ {inscricao.valorProduto.toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              {editandoStatus === inscricao.inscricaoCompleta._id ? (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Select
                                    value={novoStatus}
                                    onChange={(e) => setNovoStatus(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 120, fontSize: '0.8rem' }}
                                  >
                                    <MenuItem value="pendente">Pendente</MenuItem>
                                    <MenuItem value="processando">Processando</MenuItem>
                                    <MenuItem value="aprovado">Aprovado</MenuItem>
                                    <MenuItem value="rejeitado">Rejeitado</MenuItem>
                                    <MenuItem value="cancelado">Cancelado</MenuItem>
                                  </Select>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => salvarStatus(inscricao.inscricaoCompleta._id)}
                                    disabled={loadingStatusUpdate}
                                    sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                                  >
                                    ✓
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={cancelarEdicaoStatus}
                                    sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                                  >
                                    ✕
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {getStatusChip(inscricao.statusPagamento)}
                                  {getFormaPagamentoChip(inscricao.inscricaoCompleta?.formaPagamento || "pix")}
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => iniciarEdicaoStatus(inscricao)}
                                    sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem' }}
                                  >
                                    ✏️
                                  </Button>
                                </Box>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {formatarData(inscricao.createdAt)}
                              </Typography>
                            </Box>
                            <IconButton 
                              onClick={() => visualizarDetalhes(inscricao.inscricaoCompleta)}
                              sx={{ color: "#1976d2" }}
                              size="small"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            {inscricao.statusPagamento === "cancelado" && (
                              <IconButton 
                                onClick={() => excluirTransacao(inscricao.inscricaoCompleta._id)}
                                sx={{ color: "#d32f2f" }}
                                size="small"
                                disabled={loadingDelete}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {inscricao.tipoProduto} • {inscricao.subtipoProduto}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              ) : (
                /* Layout Desktop - Tabela */
                <TableContainer sx={{ 
                  overflowX: 'auto',
                  maxWidth: '100%',
                  display: { xs: 'none', md: 'block' },
                  '& .MuiTable-root': {
                    minWidth: 650
                  }
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 120 }}><strong>Nome Inscrito</strong></TableCell>
                        <TableCell sx={{ minWidth: 120 }}><strong>Pagador</strong></TableCell>
                        <TableCell sx={{ minWidth: 90 }}><strong>Valor Produto</strong></TableCell>
                        <TableCell sx={{ minWidth: 100 }}><strong>Tipo</strong></TableCell>
                        <TableCell sx={{ minWidth: 100 }}><strong>Subtipo</strong></TableCell>
                        <TableCell sx={{ minWidth: 90 }}><strong>Status</strong></TableCell>
                        <TableCell sx={{ minWidth: 100 }}><strong>Forma Pgto</strong></TableCell>
                        <TableCell sx={{ minWidth: 100 }}><strong>Data</strong></TableCell>
                        <TableCell sx={{ minWidth: 120, display: { xs: 'none', lg: 'table-cell' } }}><strong>ID Transação</strong></TableCell>
                        <TableCell sx={{ minWidth: 80 }} align="center"><strong>Crianças</strong></TableCell>
                        <TableCell sx={{ minWidth: 120 }}><strong>Ações</strong></TableCell>
                        <TableCell sx={{ minWidth: 90 }} align="center"><strong>Conferido</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {inscricoesFiltradas.map((inscricao, index) => (
                      <TableRow
                        key={`${inscricao.transacaoId}-${index}`}
                        sx={inscricao.conferido ? { backgroundColor: "#e8f5e9" } : undefined}
                      >
                        <TableCell>
                          {inscricao.nomeInscrito}
                        </TableCell>
                        <TableCell>
                          {inscricao.nomePagante}
                        </TableCell>
                        <TableCell>
                          R$ {inscricao.valorProduto.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {inscricao.tipoProduto}
                        </TableCell>
                        <TableCell>
                          {inscricao.subtipoProduto}
                        </TableCell>
                        <TableCell>
                          {editandoStatus === inscricao.inscricaoCompleta._id ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Select
                                value={novoStatus}
                                onChange={(e) => setNovoStatus(e.target.value)}
                                size="small"
                                sx={{ minWidth: 120 }}
                              >
                                <MenuItem value="pendente">Pendente</MenuItem>
                                <MenuItem value="processando">Processando</MenuItem>
                                <MenuItem value="aprovado">Aprovado</MenuItem>
                                <MenuItem value="rejeitado">Rejeitado</MenuItem>
                                <MenuItem value="cancelado">Cancelado</MenuItem>
                              </Select>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => salvarStatus(inscricao.inscricaoCompleta._id)}
                                disabled={loadingStatusUpdate}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                ✓
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={cancelarEdicaoStatus}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                ✕
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {getStatusChip(inscricao.statusPagamento)}
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => iniciarEdicaoStatus(inscricao)}
                                sx={{ minWidth: 'auto', p: 0.5 }}
                              >
                                ✏️
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {getFormaPagamentoChip(inscricao.inscricaoCompleta?.formaPagamento || "pix")}
                        </TableCell>
                        <TableCell>
                          {formatarData(inscricao.createdAt)}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="caption" fontFamily="monospace">
                            {inscricao.paymentId || inscricao.transacaoId?.substring(0, 8) || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {inscricao.criancas?.length > 0 ? (
                            <Chip
                              label={`👶 ${inscricao.criancas.length}`}
                              size="small"
                              sx={{ height: 22 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton 
                              onClick={() => visualizarDetalhes(inscricao.inscricaoCompleta)}
                              sx={{ color: "#1976d2" }}
                              size="small"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            {inscricao.statusPagamento === "cancelado" && (
                              <IconButton
                                onClick={() => excluirTransacao(inscricao.inscricaoCompleta._id)}
                                sx={{ color: "#d32f2f" }}
                                size="small"
                                disabled={loadingDelete}
                                title="Excluir transação cancelada"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={!!inscricao.conferido}
                            onChange={() =>
                              alternarConferido(
                                inscricao.inscricaoCompleta._id,
                                inscricao.conferido
                              )
                            }
                            disabled={conferindoIds.includes(inscricao.inscricaoCompleta._id)}
                            color="success"
                            title={
                              inscricao.conferido && inscricao.conferidoPor
                                ? `Conferido por ${inscricao.conferidoPor}`
                                : "Marcar como conferido"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {inscricoesFiltradas.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {produtoSelecionado ? 
                              (inscricoes.length === 0 ? "Nenhuma inscrição encontrada para este produto" : "Nenhuma inscrição corresponde aos filtros aplicados") 
                              : "Selecione um produto"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal de Detalhes */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              margin: { xs: 1, sm: 2 },
              maxHeight: { xs: 'calc(100vh - 16px)', sm: '90vh' },
              width: { xs: 'calc(100vw - 16px)', sm: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
            Detalhes da Inscrição
          </DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: "#f8f9fa" }}>
            {inscricaoDetalhes && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {/* Responsável */}
                <Box sx={{ ...secaoDetalheSx, flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
                  <Typography variant="overline" sx={tituloSecaoSx}>
                    Responsável
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">Nome</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {inscricaoDetalhes.nomePagante}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, wordBreak: "break-all" }}>
                      {inscricaoDetalhes.emailPagante}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Telefone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {inscricaoDetalhes.telefonePagante || "Não informado"}
                    </Typography>
                  </Box>
                </Box>

                {/* Pagamento */}
                <Box sx={{ ...secaoDetalheSx, flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" } }}>
                  <Typography variant="overline" sx={tituloSecaoSx}>
                    Pagamento
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      {getStatusChip(inscricaoDetalhes.statusPagamento)}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">Forma</Typography>
                      {getFormaPagamentoChip(inscricaoDetalhes.formaPagamento || "pix")}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">Valor Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                        R$ {inscricaoDetalhes.valorTotal.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">Data</Typography>
                      <Typography variant="body2">{formatarData(inscricaoDetalhes.createdAt)}</Typography>
                    </Box>
                    {inscricaoDetalhes.mercadoPago?.paymentId && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">ID Mercado Pago</Typography>
                        <Typography variant="caption" fontFamily="monospace">
                          {inscricaoDetalhes.mercadoPago.paymentId}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Inscritos */}
                <Box sx={{ ...secaoDetalheSx, flex: "1 1 100%" }}>
                  <Typography variant="overline" sx={tituloSecaoSx}>
                    Inscritos ({inscricaoDetalhes.inscricoes?.length || 0})
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {inscricaoDetalhes.inscricoes?.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom:
                            index < inscricaoDetalhes.inscricoes.length - 1
                              ? "1px solid"
                              : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.nomeInscrito}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.tipoProduto} - {item.subtipoProduto}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                          R$ {item.valorProduto.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Crianças */}
                {inscricaoDetalhes.criancas?.length > 0 && (
                  <Box sx={{ ...secaoDetalheSx, flex: "1 1 100%" }}>
                    <Typography variant="overline" sx={tituloSecaoSx}>
                      Crianças ({inscricaoDetalhes.criancas.length})
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {inscricaoDetalhes.criancas.map((crianca, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom:
                              index < inscricaoDetalhes.criancas.length - 1
                                ? "1px solid"
                                : "none",
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {crianca.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {crianca.idade
                              ? `${crianca.idade} ano(s)`
                              : "Idade não informada"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Múltipla Exportação */}
        <Dialog 
          open={openExportDialog} 
          onClose={() => !loadingExport && setOpenExportDialog(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Exportar Múltiplos Produtos
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              Selecione os produtos que deseja incluir na exportação consolidada:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={produtosSelecionadosExport.length === produtos.length}
                    indeterminate={produtosSelecionadosExport.length > 0 && produtosSelecionadosExport.length < produtos.length}
                    onChange={toggleTodosProdutos}
                  />
                }
                label="Selecionar todos os produtos"
                sx={{ mb: 1 }}
              />
              {produtos.map((produto) => (
                <FormControlLabel
                  key={produto._id}
                  control={
                    <Checkbox
                      checked={produtosSelecionadosExport.includes(produto._id)}
                      onChange={() => toggleProdutoExport(produto._id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {produto.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {produto.tipo} - {produto.subtipo} - R$ {produto.valor.toFixed(2)}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenExportDialog(false)}
              disabled={loadingExport}
            >
              Cancelar
            </Button>
            <Button 
              variant="contained"
              onClick={exportarCSVMultiplos}
              disabled={loadingExport || produtosSelecionadosExport.length === 0}
              startIcon={loadingExport ? <CircularProgress size={16} /> : <ExportIcon />}
            >
              {loadingExport ? "Exportando..." : "Exportar Selecionados"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </InternalLayout>
  );
}